import {
  InteractionRequiredAuthError,
  PublicClientApplication
} from '@azure/msal-browser'
import { config, scopes } from '@/config/authConfig'

/**
 * MSAL instance
 */
export const msal = new PublicClientApplication(config)

/**
 * Auth service
 */
export const Auth = {
  /**
   * Initialize and return active account
   */
  async initialize (client) {
    // Required for modern MSAL: Initialize the PublicClientApplication first
    await msal.initialize()

    // 🌟 THIS IS VITAL: This captures the login response after Microsoft redirects back here
    await msal.handleRedirectPromise()

    // Hook into application router
    if (client) {
      msal.setNavigationClient(client)
    }

    // Grab and set account if in session
    const accounts = msal.getAllAccounts()
    if (accounts?.length) {
      this.setAccount(accounts[0])
    }

    // Return any active account
    return msal.getActiveAccount()
  },

  /**
   * Login
   */
  async login () {
    const request = {
      redirectUri: config.auth.redirectUri,
      scopes,
    }
    
    // 🌟 1. Changed loginPopup to loginRedirect
    // Note: loginRedirect doesn't return a promise result here because it instantly navigates away from your page
    return msal.loginRedirect(request)
      .catch((error) => {
        if (error.errorCode === 'interaction_in_progress') {
          this.reset()
          return this.login()
        }
        throw new Error(error.errorMessage || error.message)
      })
  },

  /**
   * Logout
   */
  async logout () {
    // 🌟 2. Changed logoutPopup to logoutRedirect
    return msal.logoutRedirect({
      // Clear session from Microsoft and send them back to your redirectUri config
      postLogoutRedirectUri: config.auth.redirectUri 
    })
  },

  /**
   * Get token for api
   */
  async getToken () {
    const request = {
      scopes
    }
    return msal
      // Try getting the token silently (checks browser cache / hidden iframe)
      .acquireTokenSilent(request)

      // Attempt redirect context if silent generation fails
      .catch(async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // 🌟 3. Changed acquireTokenPopup to acquireTokenRedirect
          return msal.acquireTokenRedirect(request)
        }
        throw error
      })
      .then((result) => {
        // If acquireTokenRedirect fired, execution stops here as the page unloads/navigates away
        return result?.accessToken
      })
  },

  /**
   * Set active account
   * @private
   */
  setAccount (account) {
    msal.setActiveAccount(account)
    return account
  },

  /**
   * Escape hatch when msal gets stuck
   * @private
   */
  reset () {
    sessionStorage.clear()
  },
}
